import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { EditorFieldMap, EditorSignerRole } from "./templateEditorModel";

export type TemplateFamily = {
  id: string;
  organization_id: string;
  name: string;
  document_class: string;
  sale_path: string | null;
  created_at: string;
  updated_at: string;
};

export type TemplateVersion = {
  id: string;
  family_id: string;
  organization_id: string;
  version_number: number;
  status: "draft" | "published" | "superseded";
  pdf_storage_path: string | null;
  field_map: EditorFieldMap;
  signer_roles: EditorSignerRole[];
  signature_provider: "dropbox_sign" | "docusign" | "manual_pdf";
  source_doc_version_id: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
};

export type TemplateSubscriptionMeta = {
  publisher_org_id: string;
  mandatory: boolean;
  allowed_location_ids: string[] | null;
  read_only: true;
};

export type SignTemplateListResponse = {
  ok: true;
  families: Array<{
    family: TemplateFamily;
    versions: TemplateVersion[];
    subscription?: TemplateSubscriptionMeta;
  }>;
};

export type SignTemplateUpsertResponse = {
  ok: true;
  family: TemplateFamily;
  version: TemplateVersion;
  pdf_upload?: {
    bucket: string;
    path: string;
    token: string;
    signed_url: string;
    artifact_id: string;
    content_type: string;
  };
};

export type SignPrefillPathEntry = {
  path: string;
  label: string;
  group: string;
  sample: string;
};

export type SignPrefillRegistryResponse = {
  ok: true;
  paths: SignPrefillPathEntry[];
  sample_fixture: Record<string, unknown>;
};

export function listSignTemplates(params?: { document_class?: string; sale_path?: string }) {
  const q = new URLSearchParams();
  if (params?.document_class) q.set("document_class", params.document_class);
  if (params?.sale_path) q.set("sale_path", params.sale_path);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return manufacturingFetch<SignTemplateListResponse>(`/api/sign/template-list${suffix}`, { method: "GET" });
}

export function upsertSignTemplate(body: Record<string, unknown>) {
  return manufacturingFetch<SignTemplateUpsertResponse>("/api/sign/template-upsert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function publishSignTemplate(versionId: string) {
  return manufacturingFetch<SignTemplateUpsertResponse>("/api/sign/template-publish", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version_id: versionId }),
  });
}

export function getSignTemplatePdfUrl(versionId: string) {
  const q = new URLSearchParams({ version_id: versionId });
  return manufacturingFetch<{
    ok: true;
    signed_url: string;
    signed_expires_at: string;
    ttl_sec: number;
    version_id: string;
    family_id: string;
    status: string;
  }>(`/api/sign/template-pdf-url?${q.toString()}`, { method: "GET" });
}

export function getSignPrefillRegistry() {
  return manufacturingFetch<SignPrefillRegistryResponse>("/api/sign/prefill-registry", { method: "GET" });
}

export type SignTemplateFromDocResponse = {
  ok: true;
  family: TemplateFamily;
  version: TemplateVersion;
  reused_existing_draft: boolean;
};

export function createSignTemplateFromDoc(body: {
  document_id: string;
  version_id: string;
  export_id?: string;
}) {
  return manufacturingFetch<SignTemplateFromDocResponse>("/api/sign/template-from-doc", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type SignSubscriptionUpsertResponse = {
  ok: true;
  subscription: {
    id: string;
    publisher_org_id: string;
    subscriber_org_id: string;
    template_family_id: string;
    mandatory: boolean;
    allowed_location_ids: string[] | null;
    effective_at: string;
    ends_at: string | null;
  };
  revoked: boolean;
};

export function upsertSignTemplateSubscription(body: {
  subscriber_org_id: string;
  template_family_id: string;
  mandatory?: boolean;
  allowed_location_ids?: string[] | null;
  effective_at?: string;
  revoke?: boolean;
}) {
  return manufacturingFetch<SignSubscriptionUpsertResponse>("/api/sign/subscription-upsert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function uploadSignTemplatePdf(args: {
  signedUrl: string;
  token: string;
  file: File;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(args.signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "application/pdf",
      "x-upsert": "false",
    },
    body: args.file,
  });
  if (!res.ok) {
    return { ok: false, error: `PDF upload failed (${res.status})` };
  }
  return { ok: true };
}

export type { SignChannel } from "./signApiClient";
